let formFieldInstances = 0;

/**
 * A persistent label, help text, error and loading state around one input.
 *
 * The default slot is scoped: `attrs` carries the id and aria-* attributes
 * the input needs, so `v-bind="attrs"` wires up any native input,
 * vue-select or date-picker (both pass them through to their real control).
 * The `label-append` slot adds content inside the label after its text,
 * such as a tooltip icon.
 */
const FormField = Vue.component('form-field', {
    template: "#form-field-component-template",
    name: 'FormField',
    props: {
        label: {
            type: String,
            required: true
        },
        /**
         * Hide the label visually. It still names the input for assistive
         * tech, so this is the right choice over a placeholder-only field.
         */
        hideLabel: {
            type: Boolean,
            default: false
        },
        /**
         * Extra classes for the label, e.g. to match a page's spacing.
         */
        labelClass: {
            type: [String, Array, Object],
            default: null
        },
        help: {
            type: String,
            default: null
        },
        /**
         * Error text. Setting it marks the input invalid.
         */
        error: {
            type: String,
            default: null
        },
        required: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        loadingText: {
            type: String,
            default: 'Loading…'
        },
        /**
         * The input's id. One is generated if omitted.
         */
        id: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            uid: `form-field-${++formFieldInstances}`
        };
    },
    computed: {
        /**
         * @returns {string}
         */
        controlId() {
            return this.id || this.uid;
        },
        /**
         * @returns {string}
         */
        labelId() {
            return `${this.controlId}-label`;
        },
        /**
         * @returns {string}
         */
        helpId() {
            return `${this.controlId}-help`;
        },
        /**
         * @returns {string}
         */
        errorId() {
            return `${this.controlId}-error`;
        },
        /**
         * The slot scope.
         */
        field() {
            const describedBy = [
                this.help ? this.helpId : null,
                this.error ? this.errorId : null
            ].filter(Boolean).join(' ');

            return {
                id: this.controlId,
                invalid: !!this.error,
                attrs: {
                    'id': this.controlId,
                    'aria-describedby': describedBy || null,
                    'aria-invalid': this.error ? 'true' : null,
                    'aria-required': this.required ? 'true' : null,
                    'aria-busy': this.loading ? 'true' : null
                }
            };
        }
    }
});
